import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import AdSenseSlot from "../components/AdSlot";
import AdukooAdSlot from "../components/AdukooAdSlot";
import PullToRefresh from "../components/PullToRefresh";
import PromoBanners from "../components/PromoBanners";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, X, ArrowDownAZ, ArrowLeft, Banknote, MapPin, Loader2, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSeoMeta, SEO_BASE_URL } from "../lib/useSeoMeta";
// v79.172 — ViewToggle retired; single tile grid across the marketplace.

const PAGE_SIZE = 24;

const SORT_OPTIONS = [
    { value: "recent", label: "Most recent" },
    { value: "price_asc", label: "Price: Low → High" },
    { value: "price_desc", label: "Price: High → Low" },
    // v79.93 — Rating-first browse. Backend sorts by avg_rating desc,
    // then rating_count desc so ties break in favour of well-reviewed
    // listings over one-off 5-stars.
    { value: "rating", label: "Rating: Highest" },
];

// v79.93 — Quick "minimum stars" filter chips. Each value is passed to
// the /api/listings min_rating query param.
const RATING_CHIPS = [
    { value: "4", label: "4★ & up" },
    { value: "3", label: "3★ & up" },
];

export default function Browse() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [skip, setSkip] = useState(0);
    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // v79.172 — Single tile view across the marketplace; toggle removed.
    // On small viewports the filter panel collapses behind a button — tapping
    // "Filters" opens it; tapping outside or "Apply" closes it. Always open on md+.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const reqRef = useRef(0);

    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const condition = params.get("condition") || "";
    const country = params.get("country") || "";
    const state = params.get("state") || "";
    const city = params.get("city") || "";
    const sort = params.get("sort") || "recent";
    const minRating = params.get("min_rating") || "";
    const minPrice = params.get("min_price") || "";
    const maxPrice = params.get("max_price") || "";
    const pincodeFilter = params.get("pincode") || "";
    const brandFilter = params.get("brand") || "";
    // v79.192 — IT sub-type filter (hardware | software). Only meaningful
    // when category === "it"; ignored otherwise (never sent to backend).
    const itType = params.get("it_type") || "";

    const lat = params.get("lat") || "";
    const lng = params.get("lng") || "";
    const radiusKm = params.get("radius_km") || "";
    // Seller-type filter — pages link here as ?seller_account_type=business
    // (from the Home "Ads from registered Businesses" → View all) or
    // ?seller_account_type=individual (from the "Ads from individuals" →
    // View all). Browse passes it through to /api/listings which honours it.
    const sellerAccountType = params.get("seller_account_type") || "";
    // v78.53 — "Hide demos" toggle removed entirely: there are no demo
    // listings on the platform anymore (3,169 seeded demos wiped in v77.x),
    // so the UI control was always-off-no-op. The legacy localStorage keys
    // `adukoo_hide_demo` / `adukoo_home_show_demos` are simply ignored.

    const [detectingGeo, setDetectingGeo] = useState(false);
    // v79.210 — Founder direction (Feb 2026): Digital Stores + Sponsored
    // deals rails removed from all category pages. Sponsored deals now
    // live ONLY on the Affiliate Deals page (?category=others) via the
    // main listings feed. On that page, a sub-category chip strip lets
    // buyers narrow down to a specific vertical.
    const isAffiliatePage = (category || "") === "others";
    const dealCategory = params.get("deal_category") || "";
    // Local input state so typing stays responsive; URL syncs on 500 ms debounce.
    const [stateInput, setStateInput] = useState(state);
    const [cityInput, setCityInput] = useState(city);
    const [pincodeInput, setPincodeInput] = useState(pincodeFilter);
    const [brandInput, setBrandInput] = useState(brandFilter);
    const [minInput, setMinInput] = useState(minPrice);
    const [maxInput, setMaxInput] = useState(maxPrice);
    useEffect(() => { setStateInput(state); }, [state]);
    useEffect(() => { setCityInput(city); }, [city]);
    useEffect(() => { setPincodeInput(pincodeFilter); }, [pincodeFilter]);
    useEffect(() => { setBrandInput(brandFilter); }, [brandFilter]);
    useEffect(() => { setMinInput(minPrice); }, [minPrice]);
    useEffect(() => { setMaxInput(maxPrice); }, [maxPrice]);

    const activeCategory = useMemo(() => categories.find((c) => c.slug === category), [categories, category]);

    // v78.32 — Per-filter SEO. Browse becomes a real category page in
    // Google when canonical reflects the active facet.
    const seoLabel = sellerAccountType === "business" ? "Businesses" : sellerAccountType === "individual" ? "Individuals" : (activeCategory?.name || (condition === "new" ? "Brand-new" : condition === "old" ? "Pre-loved" : condition === "other" ? "Services" : "All"));
    const seoCanonical = (() => {
        const u = new URL(`${SEO_BASE_URL}/browse`);
        if (category) u.searchParams.set("category", category);
        if (condition) u.searchParams.set("condition", condition);
        if (sellerAccountType) u.searchParams.set("seller", sellerAccountType);
        if (country) u.searchParams.set("country", country);
        return u.toString();
    })();
    useSeoMeta({
        title: `${seoLabel} listings — Browse on Adukoo`,
        description: `Browse ${seoLabel.toLowerCase()} listings on Adukoo across 180+ countries. Used cars, real estate, jobs, electronics, fashion and services — all in one global marketplace.`,
        canonical: seoCanonical,
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${seoLabel} listings on Adukoo`,
            "url": seoCanonical,
            "isPartOf": { "@type": "WebSite", "@id": `${SEO_BASE_URL}/#website` },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_BASE_URL}/` },
                    { "@type": "ListItem", "position": 2, "name": "Browse", "item": `${SEO_BASE_URL}/browse` },
                    ...(activeCategory ? [{ "@type": "ListItem", "position": 3, "name": activeCategory.name, "item": seoCanonical }] : []),
                ],
            },
        },
    });

    useEffect(() => {
        const sellerLabel = sellerAccountType === "business" ? "Ads from registered Businesses" : sellerAccountType === "individual" ? "Ads from individuals" : "";
        const label = sellerLabel || activeCategory?.name || (condition === "new" ? "Brand-new listings" : condition === "old" ? "Pre-loved listings" : condition === "other" ? "Services & local businesses" : "All listings");
        document.title = `${label} — Browse on Adukoo`;
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/countries").then((r) => setCountries(r.data));
    }, [activeCategory, condition, sellerAccountType]);

    // v79.210 — Rails deprecated. useEffect intentionally removed;
    // sponsored deals are now inlined via the main listings feed on
    // /browse?category=others.

    const buildQs = (nextSkip) => {
        const qs = new URLSearchParams();
        if (q) qs.set("q", q);
        if (category) qs.set("category", category);
        if (condition) qs.set("condition", condition);
        if (country) qs.set("country", country);
        if (state) qs.set("state", state);
        if (city) qs.set("city", city);
        if (minPrice) qs.set("min_price", minPrice);
        if (maxPrice) qs.set("max_price", maxPrice);
        if (pincodeFilter) qs.set("pincode", pincodeFilter);
        if (brandFilter) qs.set("brand", brandFilter);
        if (category === "it" && itType) qs.set("it_type", itType);
        // v79.210 — Sub-category filter on the Affiliate Deals page.
        if (isAffiliatePage && dealCategory) qs.set("deal_category", dealCategory);
        if (lat && lng) {
            qs.set("lat", lat);
            qs.set("lng", lng);
            if (radiusKm) qs.set("radius_km", radiusKm);
        }
        if (sellerAccountType) qs.set("seller_account_type", sellerAccountType);
        if (minRating) qs.set("min_rating", minRating);
        qs.set("sort", sort);
        qs.set("limit", String(PAGE_SIZE));
        qs.set("skip", String(nextSkip));
        return qs;
    };

    // Initial / filter-changed load — extracted so PullToRefresh can call it.
    const refresh = async () => {
        const reqId = ++reqRef.current;
        setLoading(true);
        setSkip(0);
        try {
            const r = await api.get(`/listings?${buildQs(0).toString()}`);
            if (reqRef.current !== reqId) return;
            setListings(r.data.items || []);
            setTotal(r.data.total || 0);
            setHasMore(Boolean(r.data.has_more));
        } catch (err) {
            // v78.12 — Swallow transient errors (429, network blips) so they
            // don't surface as a full-page "Uncaught runtime errors" overlay
            // that hides the existing listing grid. Toast a soft hint instead.
            if (reqRef.current === reqId) {
                if (err?.response?.status === 429) {
                    toast.info("Slow down — server is rate-limiting. Try again in a moment.");
                } else if (!err?.response) {
                    toast.error("Network hiccup — retrying soon.");
                }
            }
        } finally { if (reqRef.current === reqId) setLoading(false); }
    };

    useEffect(() => {
        refresh();
        // Silent background poll — re-fetch the active filter set every 120 s
        // while the tab is visible so newly-posted ads appear without forcing
        // a manual pull-to-refresh. v78.42 — bumped 60 → 120 s in line with
        // the Home-feed bump; users hitting Browse are actively scanning,
        // they will see new ads on their next scroll anyway.
        const poll = setInterval(() => {
            if (document.visibilityState === "visible") refresh();
        }, 120_000);
        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(poll);
            document.removeEventListener("visibilitychange", onVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, category, condition, country, state, city, sort, minPrice, maxPrice, pincodeFilter, brandFilter, lat, lng, radiusKm, sellerAccountType, minRating, itType, dealCategory]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next);
    };

    // Generic debounce hook for the text inputs — keeps typing snappy.
    const useDebouncedParam = (paramKey, inputValue) => {
        useEffect(() => {
            const t = setTimeout(() => {
                const cur = params.get(paramKey) || "";
                if (inputValue !== cur) {
                    const next = new URLSearchParams(params);
                    if (inputValue) next.set(paramKey, inputValue); else next.delete(paramKey);
                    setParams(next);
                }
            }, 500);
            return () => clearTimeout(t);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [inputValue]);
    };
    useDebouncedParam("state", stateInput);
    useDebouncedParam("city", cityInput);
    useDebouncedParam("pincode", pincodeInput);
    useDebouncedParam("brand", brandInput);

    // Price range — needs a paired update (min + max in one history entry).
    useEffect(() => {
        const t = setTimeout(() => {
            const curMin = params.get("min_price") || "";
            const curMax = params.get("max_price") || "";
            if (minInput !== curMin || maxInput !== curMax) {
                const next = new URLSearchParams(params);
                const setOrDel = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
                setOrDel("min_price", minInput);
                setOrDel("max_price", maxInput);
                setParams(next);
            }
        }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minInput, maxInput]);

    const detectAndSetGeo = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation not supported by this browser");
            return;
        }
        setDetectingGeo(true);
        // High accuracy so "Near me" matches the user's actual block, not the
        // city centre — important for hyper-local categories like food / rentals.
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setDetectingGeo(false);
                const next = new URLSearchParams(params);
                next.set("lat", pos.coords.latitude.toFixed(6));
                next.set("lng", pos.coords.longitude.toFixed(6));
                next.delete("radius_km");
                setParams(next);
                toast.success("Showing Ads near you", {
                    description: `Accuracy: ±${Math.round(pos.coords.accuracy)} m — results sorted by distance.`,
                });
            },
            (err) => {
                setDetectingGeo(false);
                if (err.code === 1) {
                    toast.error("Location permission denied", { description: "Enable location in your browser settings to use Near me." });
                } else if (err.code === 3) {
                    toast.error("Location timed out", { description: "Try again outdoors or near a window." });
                } else {
                    toast.error("Could not detect your location");
                }
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
        );
    };

    const clearNearMe = () => {
        const next = new URLSearchParams(params);
        next.delete("lat"); next.delete("lng"); next.delete("radius_km");
        setParams(next);
    };

    const clearAll = () => setParams({});

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        const nextSkip = skip + PAGE_SIZE;
        setLoadingMore(true);
        try {
            const { data } = await api.get(`/listings?${buildQs(nextSkip).toString()}`);
            setListings((prev) => [...prev, ...(data.items || [])]);
            setTotal(data.total || 0);
            setHasMore(Boolean(data.has_more));
            setSkip(nextSkip);
        } catch (err) {
            // v78.12 — Same defensive swallow as `refresh()`. Without this a
            // single 429 from infinite-scroll spamming /api/listings would
            // throw to React's error boundary and freeze the page.
            if (err?.response?.status === 429) {
                toast.info("Loading paused — too many requests. Scroll again in a few seconds.");
            } else if (!err?.response) {
                toast.error("Network hiccup — try again.");
            }
            // Keep hasMore=true so the user can retry; just reset loadingMore.
        } finally { setLoadingMore(false); }
    };

    // Count active filters (excludes free-text q + sort) so we can show a badge
    // on the mobile "Filters" pill — gives users a quick clue that filters are on.
    const activeFilterCount = [category, condition, country, state, city, minPrice, maxPrice, pincodeFilter, brandFilter, sellerAccountType, lat && lng ? "geo" : ""].filter(Boolean).length;

    return (
        <PullToRefresh onRefresh={refresh}>
        <div data-testid="browse-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8 w-full">
            {/* v79.5 — Removed duplicate Browse-page Back chip. The global
                <BackButton /> in App.js renders this on every non-Home
                page and uses the same navigate(-1) logic + safe-history
                fallback. */}
            <div className="flex flex-wrap items-center gap-2.5 mb-4">
                <h1 className="font-display text-2xl sm:text-3xl font-black text-aduku-navy">
                    {sellerAccountType === "business" ? "Ads from registered Businesses" : sellerAccountType === "individual" ? "Ads from individuals" : sellerAccountType === "affiliate" ? "Sponsored Ads" : (activeCategory ? activeCategory.name : "All Listings")}
                </h1>
                {q && <span className="px-2.5 py-1 rounded-full bg-aduku-navy text-white text-xs truncate max-w-[40vw]">"{q}"</span>}
                {sellerAccountType && (
                    <button
                        type="button"
                        onClick={() => update("seller_account_type", "")}
                        data-testid="seller-type-chip-clear"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-aduku-yellow text-aduku-navy text-xs font-bold hover:bg-aduku-yellow/80 transition"
                        title="Remove seller-type filter"
                    >
                        {sellerAccountType === "business" ? "Businesses only" : sellerAccountType === "individual" ? "Individuals only" : "Sponsored deals only"}
                        <X className="w-3 h-3" />
                    </button>
                )}
                <span className="text-aduku-navy/60 text-sm ml-auto whitespace-nowrap" data-testid="results-count">
                    {total} result{total === 1 ? "" : "s"}
                </span>
                {/* v79.172 — List view retired; toggle removed so users
                    stay in the optimised tile grid where hero images
                    fit consistently across all devices. */}
            </div>

            {/* Admin-controlled affiliate banner rail — between header and grid. */}
            <PromoBanners placement="browse" limit={1} />

            {/* v79.171 — Adukoo in-house ad. Sits between the header row
                and the product grid so it feels native to the browsing
                flow. Renders nothing when no targeted ad is active. */}
            <AdukooAdSlot slot="browse_feed" className="my-3" />

            {/* Mobile-only "Filters" toggle pill — desktop keeps the sidebar always open.
                v79.4 — Tightened paddings and added `min-w-0` so the
                right-most "Login" button no longer gets clipped on
                360-380 px Android viewports. */}
            <div className="md:hidden mb-3 flex items-center gap-2">
                <button
                    data-testid="toggle-filters-mobile"
                    onClick={() => setFiltersOpen((v) => !v)}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-aduku-navy text-aduku-yellow font-bold text-sm hover:bg-aduku-navy-700 active:scale-95 transition"
                >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-aduku-yellow text-aduku-navy text-[11px] font-extrabold">
                            {activeFilterCount}
                        </span>
                    )}
                </button>
                {activeFilterCount > 0 && (
                    <button
                        onClick={clearAll}
                        data-testid="clear-filters-btn-mobile"
                        className="text-xs text-aduku-navy/70 hover:text-aduku-navy inline-flex items-center gap-1"
                    >
                        <X className="w-3 h-3" /> Clear all
                    </button>
                )}
            </div>

            <div className="grid md:grid-cols-[240px_1fr] gap-4 md:gap-6">
                {/* v79.25 — Filters are now unconditionally rendered
                    everywhere: desktop sticky sidebar inline in the grid,
                    mobile bottom-sheet PORTALED into document.body via
                    createPortal so it escapes PullToRefresh's transform
                    stacking context and always renders above inline ads. */}
                {(() => {
                  const filterFormContent = (
                    <div className="space-y-3.5">
                        {/* v79.207 — Founder direction (Feb 2026): "Remove
                            the global category dropdown; users pick a
                            category via chips or URL and cannot flip back
                            to 'all' inside the sidebar." Category is now
                            immutable within a Browse session — to switch
                            categories the user must navigate back to the
                            Home chips grid. */}

                        {/* 1. Seller type — Business / Individual / Any.
                            v79.220 — Founder direction: "Under Jobs
                            posting category, Seller type → Posted by.
                            Check all categories for suitable wording."
                            Label + option copy now context-shifts by
                            category so buyers see the right vocabulary:
                            employers hire, agents list, brokers list,
                            candidates apply, service providers offer, …
                        */}
                        {(() => {
                            let sellerTypeCopy = { label: "Seller type", all: "Any seller", business: "Registered Businesses only", individual: "Individuals only" };
                            if (category === "jobs" || category === "job-seeker") {
                                sellerTypeCopy = { label: "Posted by", all: "Anyone", business: "Companies · Recruiters", individual: "Individuals · Freelancers" };
                            } else if (category === "real-estate") {
                                sellerTypeCopy = { label: "Listed by", all: "Anyone", business: "Agents · Builders", individual: "Owners only" };
                            } else if (category === "vehicles" || category === "vehicle-rental") {
                                sellerTypeCopy = { label: "Listed by", all: "Anyone", business: "Dealers · Showrooms", individual: "Direct owners" };
                            } else if (category === "matrimony") {
                                sellerTypeCopy = { label: "Posted by", all: "Anyone", business: "Bureaux · Matchmakers", individual: "Self · Family" };
                            } else if (category === "services" || category === "home-maid" || category === "hospitals" || category === "beauty-parlours" || category === "financial-services" || category === "insurance-distributor" || category === "mutual-fund-distributor") {
                                sellerTypeCopy = { label: "Provided by", all: "Anyone", business: "Registered Businesses", individual: "Independent providers" };
                            } else if (category === "food" || category === "bakery" || category === "sweet-shops" || category === "iron-shop") {
                                sellerTypeCopy = { label: "Ordered from", all: "Anywhere", business: "Established outlets", individual: "Home kitchens" };
                            } else if (category === "movies-ott") {
                                sellerTypeCopy = { label: "Announced by", all: "Anyone", business: "Studios · Distributors", individual: "Independent creators" };
                            } else if (category === "education") {
                                sellerTypeCopy = { label: "Offered by", all: "Anyone", business: "Institutions · Coaching", individual: "Independent tutors" };
                            }
                            return (
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">{sellerTypeCopy.label}</label>
                            <Select value={sellerAccountType || "all"} onValueChange={(v) => update("seller_account_type", v === "all" ? "" : v)}>
                                <SelectTrigger data-testid="filter-seller-type" className="mt-1.5 h-9">
                                    <SelectValue placeholder="Any" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all" data-testid="filter-seller-type-all">{sellerTypeCopy.all}</SelectItem>
                                    <SelectItem value="business" data-testid="filter-seller-type-business">{sellerTypeCopy.business}</SelectItem>
                                    <SelectItem value="individual" data-testid="filter-seller-type-individual">{sellerTypeCopy.individual}</SelectItem>
                                    <SelectItem value="affiliate" data-testid="filter-seller-type-affiliate">Sponsored deals (affiliate)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                            );
                        })()}

                        {/* 1b. Sponsored deals sub-category — sidebar Select
                            (v79.222). Only appears on the Affiliate Deals
                            page (?category=others). Buyers can narrow all
                            Sponsored deals to a specific underlying
                            vertical (Mobiles, Electronics, Vehicles, …).
                            Replaces the old yellow inline panel that used
                            to sit above the sort bar. */}
                        {isAffiliatePage && (
                            <div>
                                <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Sponsored deal category</label>
                                <Select value={dealCategory || "all"} onValueChange={(v) => update("deal_category", v === "all" ? "" : v)}>
                                    <SelectTrigger data-testid="filter-affiliate-deal-category" className="mt-1.5 h-9">
                                        <SelectValue placeholder="All deals" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        <SelectItem value="all" data-testid="filter-affiliate-deal-category-all">All deals</SelectItem>
                                        {categories
                                            .filter((c) => c.slug !== "others" && c.slug !== "job-seeker")
                                            .map((c) => (
                                                <SelectItem
                                                    key={c.slug}
                                                    value={c.slug}
                                                    data-testid={`filter-affiliate-deal-category-${c.slug}`}
                                                >
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* 2. Brand — manual text (paired with Category) */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Brand</label>
                            <Input
                                data-testid="filter-brand"
                                value={brandInput}
                                onChange={(e) => setBrandInput(e.target.value)}
                                placeholder="e.g. Apple, Samsung, Tata"
                                className="mt-1.5 h-9 text-sm"
                            />
                        </div>

                        {/* 3. Country (worldwide) */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Country</label>
                            <Select value={country || "all"} onValueChange={(v) => update("country", v === "all" ? "" : v)}>
                                <SelectTrigger data-testid="filter-country" className="mt-1.5 h-9">
                                    <SelectValue placeholder="All countries" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    <SelectItem value="all">All countries</SelectItem>
                                    {countries.map((c) => (
                                        <SelectItem key={c.code} value={c.code} data-testid={`filter-country-${c.code}`}>
                                            {c.flag} {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* 3. State — manual text */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">State / Province / Region</label>
                            <Input
                                data-testid="filter-state"
                                value={stateInput}
                                onChange={(e) => setStateInput(e.target.value)}
                                placeholder="e.g. California, Bavaria, Karnataka"
                                className="mt-1.5 h-9 text-sm"
                            />
                        </div>

                        {/* 4. City — manual text */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">City</label>
                            <Input
                                data-testid="filter-city"
                                value={cityInput}
                                onChange={(e) => setCityInput(e.target.value)}
                                placeholder="e.g. Bengaluru, London, Dubai"
                                className="mt-1.5 h-9 text-sm"
                            />
                        </div>

                        {/* 5. Pincode / Zip — manual text */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Pincode / Zip</label>
                            <Input
                                data-testid="filter-pincode"
                                value={pincodeInput}
                                onChange={(e) => setPincodeInput(e.target.value.replace(/[^A-Za-z0-9\- ]/g, "").slice(0, 12))}
                                placeholder="e.g. 560001 / SW1A"
                                className="mt-1.5 h-9 text-sm"
                            />
                            <p className="mt-1 text-[10px] text-aduku-navy/50">Partial codes work — "560" matches all 560xxx.</p>
                        </div>

                        {/* 6. Condition — hidden entirely for IT/Software
                            (v79.207) since software has no "New/Used"
                            semantics. Backend already ignores condition
                            for jobs/services; we mask the UI here for
                            software to remove confusion. */}
                        {!(category === "it" && itType === "software") && (
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Condition</label>
                            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                                {[
                                    { v: "", l: "All" },
                                    { v: "new", l: "New" },
                                    { v: "old", l: "Used" },
                                ].map((o) => (
                                    <button
                                        key={o.v || "all"}
                                        onClick={() => update("condition", o.v)}
                                        data-testid={`filter-condition-${o.v || "all"}`}
                                        className={`h-9 rounded-full text-xs font-semibold border transition ${condition === o.v
                                            ? "bg-aduku-navy text-white border-aduku-navy"
                                            : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"
                                            }`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* v79.192 — IT sub-type filter. Only shown when the
                            active category is `it` (merged Hardware +
                            Software). Backend translates ?it_type=hardware
                            to `meta.it_type = "hardware"`. */}
                        {category === "it" && (
                            <div>
                                <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">IT type</label>
                                <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                                    {[
                                        { v: "", l: "All" },
                                        { v: "hardware", l: "Hardware" },
                                        { v: "software", l: "Software" },
                                    ].map((o) => (
                                        <button
                                            key={o.v || "all"}
                                            onClick={() => update("it_type", o.v)}
                                            data-testid={`filter-it-type-${o.v || "all"}`}
                                            className={`h-9 rounded-full text-xs font-semibold border transition ${itType === o.v
                                                ? "bg-aduku-navy text-white border-aduku-navy"
                                                : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"
                                                }`}
                                        >
                                            {o.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 7. Price range */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Price range</label>
                            <div className="mt-1.5 grid grid-cols-2 gap-2">
                                <div className="relative">
                                    <Banknote className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-aduku-navy/50" />
                                    <Input
                                        data-testid="filter-min-price"
                                        inputMode="numeric"
                                        value={minInput}
                                        onChange={(e) => setMinInput(e.target.value.replace(/\D/g, ""))}
                                        placeholder="Min"
                                        className="pl-7 h-9 text-sm"
                                    />
                                </div>
                                <div className="relative">
                                    <Banknote className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-aduku-navy/50" />
                                    <Input
                                        data-testid="filter-max-price"
                                        inputMode="numeric"
                                        value={maxInput}
                                        onChange={(e) => setMaxInput(e.target.value.replace(/\D/g, ""))}
                                        placeholder="Max"
                                        className="pl-7 h-9 text-sm"
                                    />
                                </div>
                            </div>
                            {(minInput || maxInput) && (
                                <button
                                    data-testid="clear-price-btn"
                                    onClick={() => { setMinInput(""); setMaxInput(""); }}
                                    className="mt-1.5 text-[11px] text-aduku-navy/60 hover:text-aduku-navy inline-flex items-center gap-1"
                                >
                                    <X className="w-3 h-3" /> Clear price
                                </button>
                            )}
                        </div>

                        {/* 8. Near me — with radius picker (v79.208) */}
                        <div>
                            <label className="text-[11px] font-semibold text-aduku-navy/70 uppercase tracking-wider">Near me</label>
                            <button
                                type="button"
                                data-testid="detect-location-btn"
                                onClick={detectAndSetGeo}
                                disabled={detectingGeo}
                                className="mt-1.5 w-full h-9 rounded-full text-xs font-semibold border border-aduku-border bg-white text-aduku-navy hover:border-aduku-navy inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                            >
                                {detectingGeo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                                {lat && lng ? "Update my location" : "Use my precise location"}
                            </button>
                            {lat && lng && (
                                <>
                                    {/* v79.208 — Founder direction: improve
                                        Near-me accuracy. Explicit radius
                                        picker lets buyers scope by 2 km
                                        (walking) → 500 km (regional). We
                                        default to 25 km which covers most
                                        metro-area commutes. */}
                                    <div className="mt-2">
                                        <label className="text-[10px] font-semibold text-aduku-navy/60 uppercase tracking-wider">Radius</label>
                                        <div className="mt-1 grid grid-cols-6 gap-1">
                                            {[
                                                { v: "2", l: "2km" },
                                                { v: "5", l: "5km" },
                                                { v: "10", l: "10km" },
                                                { v: "25", l: "25km" },
                                                { v: "100", l: "100km" },
                                                { v: "", l: "Any" },
                                            ].map((o) => {
                                                const active = (radiusKm || "") === o.v;
                                                return (
                                                    <button
                                                        key={o.v || "any"}
                                                        onClick={() => update("radius_km", o.v)}
                                                        data-testid={`radius-chip-${o.v || "any"}`}
                                                        className={`h-8 rounded-full text-[10px] font-bold border transition ${active
                                                            ? "bg-aduku-navy text-white border-aduku-navy"
                                                            : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"
                                                        }`}
                                                    >
                                                        {o.l}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-aduku-navy/60">
                                        <span>Sorted by distance{radiusKm ? ` · within ${radiusKm} km` : ""}</span>
                                        <button
                                            onClick={clearNearMe}
                                            data-testid="clear-near-me"
                                            className="text-aduku-navy/70 hover:text-aduku-navy inline-flex items-center gap-0.5"
                                        >
                                            <X className="w-3 h-3" /> Clear
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Mobile "Apply" button — closes the panel so the user sees the results */}
                        <button
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                            data-testid="apply-filters-mobile"
                            className="md:hidden w-full h-10 mt-2 rounded-full bg-aduku-yellow text-aduku-navy font-bold text-sm hover:bg-aduku-yellow-600 active:scale-[0.98] transition"
                        >
                            Show {total} result{total === 1 ? "" : "s"}
                        </button>
                    </div>
                  );

                  // The filter form body is identical on desktop sidebar +
                  // mobile bottom-sheet, so we compute it once and hand a
                  // reference to both render sites.
                  const filterFormBody = (
                    <>
                    {/* Drag handle (mobile only) — visual cue this is a sheet. */}
                    <div className="md:hidden mx-auto mb-2 h-1 w-10 rounded-full bg-aduku-navy/15" aria-hidden />
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold text-aduku-navy text-sm">Filters</h3>
                        <div className="flex items-center gap-3">
                            <button onClick={clearAll} data-testid="clear-filters-btn" className="text-[11px] text-aduku-navy/60 hover:text-aduku-navy inline-flex items-center gap-1">
                                <X className="w-3 h-3" /> Clear
                            </button>
                            <button
                                onClick={() => setFiltersOpen(false)}
                                className="md:hidden text-aduku-navy/60 hover:text-aduku-navy"
                                data-testid="close-filters-mobile"
                                aria-label="Close filters"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {filterFormContent}
                    </>
                  );

                  return (
                    <>
                    {/* Desktop sticky sidebar — inline in grid, `md:block` only. */}
                    <aside
                        data-testid="browse-filters"
                        className="hidden md:block bg-white border border-aduku-border rounded-xl p-3 md:p-4 md:sticky md:top-24 z-10 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto"
                        style={{ scrollbarGutter: "stable" }}
                    >
                        {filterFormBody}
                    </aside>

                    {/* Mobile bottom-sheet — PORTALED to body so it escapes
                        PullToRefresh's `transform: translateY(...)` stacking
                        context, allowing `position: fixed` to map to the
                        actual viewport above every inline AdSense/AdMob frame. */}
                    {filtersOpen && createPortal(
                      <>
                        <div
                            className="fixed inset-0 bg-black/45 md:hidden"
                            style={{ zIndex: 9998 }}
                            onClick={() => setFiltersOpen(false)}
                            data-testid="browse-filters-backdrop"
                            aria-hidden
                        />
                        <aside
                            data-testid="browse-filters-mobile"
                            className="fixed inset-x-0 bottom-0 bg-white border border-aduku-border rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto md:hidden"
                            style={{ scrollbarGutter: "stable", zIndex: 9999 }}
                        >
                            {filterFormBody}
                        </aside>
                      </>,
                      document.body
                    )}
                    </>
                  );
                })()}

                {/* Results */}
                <div className="min-w-0">
                    {/* v79.210 — Category-scoped Digital Stores rail + per-
                        category Sponsored rail have been removed. Sponsored
                        deals now live ONLY on the Affiliate Deals page
                        (?category=others) and surface via the main
                        listings feed. Users can still discover storefronts
                        via /digital-stores. v79.222 — the previous inline
                        "Filter Sponsored deals by category" yellow panel
                        has moved into the sidebar filter box as a Select
                        dropdown (see filter-affiliate-deal-category). */}

                    {/* Sort bar + rating chips */}
                    <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="rating-filter-chips">
                            {RATING_CHIPS.map((chip) => {
                                const active = minRating === chip.value;
                                return (
                                    <button
                                        type="button"
                                        key={chip.value}
                                        data-testid={`rating-chip-${chip.value}`}
                                        onClick={() => update("min_rating", active ? "" : chip.value)}
                                        className={`h-8 px-3 rounded-full text-xs font-bold border transition-colors ${active
                                            ? "bg-aduku-yellow text-aduku-navy border-aduku-yellow-600 shadow-sm"
                                            : "bg-white text-aduku-navy/70 border-aduku-border hover:border-aduku-navy/50"}`}
                                    >
                                        {chip.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-2">
                            <ArrowDownAZ className="w-4 h-4 text-aduku-navy/60 shrink-0" />
                            <span className="text-xs text-aduku-navy/60 font-semibold uppercase tracking-wider hidden xs:inline">Sort by</span>
                            <Select value={sort} onValueChange={(v) => update("sort", v === "recent" ? "" : v)}>
                                <SelectTrigger data-testid="sort-select" className="w-44 sm:w-48 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SORT_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2 sm:gap-3">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="bg-white rounded-xl border border-aduku-border p-4 animate-pulse">
                                    <div className="aspect-[4/3] bg-aduku-bg rounded" />
                                    <div className="h-4 bg-aduku-bg rounded mt-3 w-1/2" />
                                    <div className="h-3 bg-aduku-bg rounded mt-2 w-3/4" />
                                </div>
                            ))}
                        </div>
                    ) : listings.length === 0 ? (
                        <div className="bg-white border border-aduku-border rounded-xl p-10 sm:p-12 text-center">
                            <Search className="w-10 h-10 mx-auto text-aduku-navy/30" />
                            <div className="font-display font-bold text-xl mt-3 text-aduku-navy">No results</div>
                            <p className="text-aduku-navy/60 mt-1">Try a different search or clear filters.</p>
                            <Link to="/post"><Button className="mt-5 aduku-yellow-btn hover:bg-aduku-yellow-600">Post a listing</Button></Link>
                        </div>
                    ) : (
                        <>
                            {/* v78.45 — AdSense top-of-results slot.
                                Returns null when env is empty. Position
                                chosen for Browse / Search because users
                                landing here have stated intent (query or
                                category) — contextually-matched display
                                ads convert well at this entry point. */}
                            <AdSenseSlot
                                slot={process.env.REACT_APP_ADSENSE_SLOT_BROWSE_TOP || ""}
                                testId="adsense-browse-top"
                                className="mb-3"
                                minHeightClass="min-h-[100px] sm:min-h-[120px]"
                            />
                        {/* v79.25 — Grid switched from breakpoint-percentage
                            columns to auto-fill with a fixed 160 px minimum.
                            Reason: on mobile Home rails render tiles at
                            ~170 px, but the old `grid-cols-2 sm:grid-cols-3
                            lg:grid-cols-4` scheme made those same tiles
                            ~250-300 px wide on desktop Browse — inconsistent
                            "view all" size the founder called out. auto-fill
                            keeps every tile 160-190 px across every viewport
                            (mobile phones, tablets, laptops, big screens,
                            TVs) so the entire marketplace uses one visual
                            tile size. `auto-rows-fr` keeps rows uniform
                            when card content varies in height. */}
                        {/* v79.172 — Founder direction: "Since high-quality
                            image display is critical to the user experience,
                            I am open to removing the current list view
                            option … consolidate the interface into a single,
                            optimized view that ensures all ad images are
                            fully visible and properly formatted."
                            → List view retired across the marketplace. Every
                            category now renders the tile grid so hero images
                            display consistently on Web, iOS Safari and the
                            Capacitor Android WebView (which had the worst
                            list-view scaling bugs). */}
                        <div
                            className="grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2 sm:gap-3"
                            data-testid="browse-listings-tile"
                        >
                            {listings.map((l) => <ProductCard key={l.id} listing={l} />)}
                            </div>
                            {/* v79.208 — mid-scroll revenue slot between the
                                grid and the Load more button. Renders nothing
                                when no ad targets this slot. */}
                            <AdukooAdSlot slot="browse_between_rows" className="mt-5" />
                            {hasMore && (
                                <div className="mt-8 text-center">
                                    <Button
                                        data-testid="load-more-btn"
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        className="rounded-full bg-aduku-navy text-white hover:bg-aduku-navy-700 h-11 px-8"
                                    >
                                        {loadingMore ? "Loading..." : `Load more (${total - listings.length} left)`}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
        </PullToRefresh>
    );
}
